import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { Client, IMessage } from '@stomp/stompjs';
import * as SockJS from 'sockjs-client';
import { API_ENDPOINTS } from '../config/api-endpoints';
import { API_URLS } from '../config/api-urls';
import { AuthService } from './auth.service';
import { ChatSession, ChatMessage, SendMessageRequest } from '../models';
export { ChatSession, ChatMessage, SendMessageRequest } from '../models';

/**
 * Wraps the livechat microservice: REST for session/history + STOMP over SockJS
 * for real-time delivery. All URLs are routed through the API gateway
 * (`/livechat/**` → `/api/v1/livechat/**` after rewrite).
 */

/**
 * The SockJS endpoint, addressed directly at the livechat service rather than
 * through the API gateway.
 *
 * Spring Cloud Gateway MVC is blocking and cannot proxy a WebSocket upgrade, so
 * routing the socket through it (`${GATEWAY}/livechat/ws-chat`) fails every
 * SockJS transport in turn — 401/403 on the xhr transports, a rejected upgrade
 * on websocket — and the client reconnects forever. nginx publishes the
 * service's own path, `/api/v1/livechat/ws-chat`, with the upgrade headers set,
 * which completes the handshake. Derived from the gateway's origin so it follows
 * whatever host the app was built for.
 */
function livechatSocketUrl(): string {
  const base = API_URLS.GATEWAY;
  try {
    return `${new URL(base).origin}/api/v1/livechat/ws-chat`;
  } catch {
    // Relative or malformed base (dev builds): fall back to the current origin.
    return `${window.location.origin}/api/v1/livechat/ws-chat`;
  }
}

@Injectable({ providedIn: 'root' })
export class LivechatService implements OnDestroy {

  private stompClient: Client | null = null;
  private messageSubject = new Subject<ChatMessage>();
  private currentEmailSubscription: string | null = null;
  messages$ = this.messageSubject.asObservable();

  constructor(private http: HttpClient, private auth: AuthService) {}

  // ---------- Session lifecycle ----------

  /**
   * Customer entry point: start (or resume) a chat with a bar.
   * Customer identity is taken from the JWT server-side; the caller only
   * needs the bar id from the QR-code URL.
   */
  startChatWithBar(barId: string): Observable<ChatSession> {
    return this.http.post<ChatSession>(
      `${API_URLS.GATEWAY}${API_ENDPOINTS.LIVECHAT.START_WITH_BAR(barId)}`,
      null
    );
  }

  /** Explicit create — kept for admin/testing paths that pass all 4 fields. */
  createChatSession(body: {
    userId: number;
    serviceRegistrationId: number;
    emailProvider: string;
    emailCustomer: string;
  }): Observable<ChatSession> {
    return this.http.post<ChatSession>(
      `${API_URLS.GATEWAY}${API_ENDPOINTS.LIVECHAT.CREATE_SESSION}`,
      body
    );
  }

  /** Sessions I own as a customer (JWT-scoped). */
  mySessions(): Observable<ChatSession[]> {
    return this.http.get<ChatSession[]>(
      `${API_URLS.GATEWAY}${API_ENDPOINTS.LIVECHAT.MINE}`
    );
  }

  /** Sessions incoming to me as a staff member (JWT-scoped). */
  incomingSessions(): Observable<ChatSession[]> {
    return this.http.get<ChatSession[]>(
      `${API_URLS.GATEWAY}${API_ENDPOINTS.LIVECHAT.INCOMING}`
    );
  }

  getSessionsByUser(userId: number | string): Observable<ChatSession[]> {
    return this.http.get<ChatSession[]>(
      `${API_URLS.GATEWAY}${API_ENDPOINTS.LIVECHAT.BY_USER}`,
      { params: { id: userId.toString() } }
    );
  }

  getSessionsByProvider(providerEmail: string): Observable<ChatSession[]> {
    return this.http.get<ChatSession[]>(
      `${API_URLS.GATEWAY}${API_ENDPOINTS.LIVECHAT.BY_PROVIDER}`,
      { params: { providerEmail } }
    );
  }

  getSessionsByBoth(providerId: number | string, userId: number | string): Observable<ChatSession[]> {
    return this.http.get<ChatSession[]>(
      `${API_URLS.GATEWAY}${API_ENDPOINTS.LIVECHAT.BY_BOTH(providerId.toString(), userId.toString())}`
    );
  }

  getInProgressSession(providerId: number | string, userId: number | string): Observable<ChatSession> {
    return this.http.get<ChatSession>(
      `${API_URLS.GATEWAY}${API_ENDPOINTS.LIVECHAT.IN_PROGRESS(providerId.toString(), userId.toString())}`
    );
  }

  getMessagesBySession(sessionId: number | string): Observable<ChatMessage[]> {
    return this.http.get<ChatMessage[]>(
      `${API_URLS.GATEWAY}${API_ENDPOINTS.LIVECHAT.MESSAGES}`,
      { params: { idSession: sessionId.toString() } }
    );
  }

  // ---------- Real-time (STOMP over SockJS) ----------

  /**
   * Connect and subscribe to messages addressed to the current authenticated
   * user's email. Messages routed via `/topic/user.{email}` are pushed onto
   * {@link messages$}. Reconnects are handled by stompjs.
   */
  async connect(): Promise<void> {
    if (this.stompClient?.active) return;

    const [token, email] = await Promise.all([
      this.auth.getAccessToken(),
      this.auth.getCurrentUserEmail(),
    ]);
    if (!email) throw new Error('livechat: no authenticated user');
    this.currentEmailSubscription = email;

    this.stompClient = new Client({
      webSocketFactory: () =>
        new (SockJS as any)(livechatSocketUrl()),
      connectHeaders: token ? { Authorization: `Bearer ${token}` } : {},
      reconnectDelay: 5000,
      onConnect: () => {
        this.stompClient!.subscribe(
          API_ENDPOINTS.LIVECHAT.STOMP_TOPIC_USER(email),
          (msg: IMessage) => {
            try {
              this.messageSubject.next(JSON.parse(msg.body) as ChatMessage);
            } catch {
              // ignore malformed frames
            }
          }
        );
      },
    });
    this.stompClient.activate();
  }

  /**
   * Send a message on an active chat session. Prefers STOMP when the client
   * is connected; falls back to the REST /chat/send endpoint so messaging
   * still works when the gateway does not proxy WebSockets. In both cases
   * the server overrides originEmail from the caller identity, so it cannot
   * be spoofed here.
   */
  sendMessage(sessionId: number | string, text: string): Observable<ChatMessage> | void {
    const payload: SendMessageRequest = {
      chatSessionId: sessionId.toString(),
      chatMessage: text,
      origin: 'web',
      originEmail: this.currentEmailSubscription ?? 'anonymous',
    };
    if (this.stompClient?.connected) {
      this.stompClient.publish({
        destination: API_ENDPOINTS.LIVECHAT.STOMP_APP_SEND,
        body: JSON.stringify(payload),
      });
      return;
    }
    // REST fallback — returns the persisted ChatMessage the caller can
    // append to their local view immediately.
    return this.http.post<ChatMessage>(
      `${API_URLS.GATEWAY}${API_ENDPOINTS.LIVECHAT.SEND_MESSAGE}`,
      payload
    );
  }

  disconnect(): void {
    this.stompClient?.deactivate();
    this.stompClient = null;
    this.currentEmailSubscription = null;
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.messageSubject.complete();
  }
}
